/**
 * Tool call phrase resolver.
 *
 * `getToolDetail(toolName, input)` returns a short human-readable phrase
 * describing what a tool call does, used in the chain-of-thought timeline
 * and in the compact tool-call card header. The goal is that the row
 * reads like a sentence — "Listing runs", "Retrieving step 5", "Reading
 * README.md" — instead of an opaque "Using Run Viewer..." for every call.
 *
 * When no friendly phrase can be derived, returns `null` and callers fall
 * back to their default label (typically `Using {displayName}...`).
 *
 * Contract:
 * - When the return value is non-null, it is a COMPLETE sentence and should
 *   REPLACE the display name in the rendered row, not be appended to it.
 * - Phrases are in present continuous tense (matches the "Thinking..." /
 *   running-action feel of the CoT) and contain no trailing punctuation.
 * - Keep phrases short — this runs in a truncated row, so long subjects
 *   are trimmed.
 */

// ─── Helpers ────────────────────────────────────────────────────────────────

/**
 * A path is considered "internal" if any of its segments starts with a dot.
 * This matches the unix dotfile convention and hides scratch locations like
 * `.tool_outputs/...`, `.cache/...`, `.git/...` from CoT display.
 */
function isInternalPath(path: string): boolean {
  return path
    .split('/')
    .some((segment) => segment.length > 0 && segment.startsWith('.'));
}

/** Shortens a string to a max length with an ellipsis if needed. */
function clip(value: string, max: number): string {
  return value.length > max ? `${value.slice(0, max - 1)}…` : value;
}

/** Reads the first non-empty string field among the given keys. */
function pickString(
  input: Record<string, unknown>,
  keys: readonly string[],
): string | undefined {
  for (const k of keys) {
    const v = input[k];
    if (typeof v === 'string' && v.length > 0) return v;
  }
  return undefined;
}

// ─── Per-tool phrase resolvers ──────────────────────────────────────────────

type PhraseResolver = (input: Record<string, unknown>) => string | null;

/**
 * Run Viewer — inspects agent runs (list runs / list steps / get step).
 * Input shapes:
 *   { operation: 'list', targetAgentId, limit? }
 *   { operation: 'list_steps', targetRunId }
 *   { operation: 'get_step', stepIndex, targetRunId }
 *   { operation: 'get_run', targetRunId }
 */
const runViewerResolver: PhraseResolver = (input) => {
  const op = String(input.operation ?? '');
  switch (op) {
    case 'list':
      return 'Listing runs';
    case 'list_steps':
      return 'Listing run steps';
    case 'get_step': {
      const idx = input.stepIndex;
      if (typeof idx === 'number') return `Retrieving step ${idx}`;
      return 'Retrieving a run step';
    }
    case 'get_run':
      return 'Retrieving run details';
    default:
      return op ? `Running Run Viewer (${op})` : null;
  }
};

/**
 * Agent Editor — CRUD for agents.
 * Input shapes: { operation: 'list' | 'get' | 'create' | 'update' | 'delete', ... }
 */
const agentEditorResolver: PhraseResolver = (input) => {
  const op = String(input.operation ?? '');
  switch (op) {
    case 'list':
      return 'Listing agents';
    case 'get':
      return 'Retrieving agent details';
    case 'create':
      return 'Creating an agent';
    case 'update':
      return 'Updating an agent';
    case 'delete':
      return 'Deleting an agent';
    default:
      return op ? `Editing agents (${op})` : null;
  }
};

/**
 * Files — reads/writes/queries VFS files (also handles SQLite via action=sql).
 * Input shapes:
 *   { action: 'read' | 'write' | 'delete' | 'list', path }
 *   { action: 'query', path, expression, pipe? }
 *   { action: 'sql', path, query, params? }
 */
const filesResolver: PhraseResolver = (input) => {
  const action = String(input.action ?? input.operation ?? '');
  let path = pickString(input, ['path', 'file_path', 'filePath', 'filename']);
  // Hide cryptic internal paths like `.tool_outputs/tol-...txt`. Without a
  // path we still produce a generic phrase so the row reads sensibly.
  const pathVisible = path && !isInternalPath(path) ? path : undefined;
  const short = pathVisible ? clip(pathVisible, 40) : undefined;

  switch (action) {
    case 'read':
      return short ? `Reading ${short}` : 'Reading a file';
    case 'write':
      return short ? `Writing ${short}` : 'Writing a file';
    case 'delete':
      return short ? `Deleting ${short}` : 'Deleting a file';
    case 'list':
      return short ? `Listing ${short}` : 'Listing files';
    case 'query':
      return short ? `Querying ${short}` : 'Querying a file';
    case 'sql':
      return short ? `Running SQL on ${short}` : 'Running a SQL query';
    case '':
      return short ? `Using file ${short}` : null;
    default:
      return short ? `${sentence(action)} ${short}` : sentence(action);
  }
};

/** Capitalizes the first letter of a verb so it reads as a sentence start. */
function sentence(verb: string): string {
  if (!verb) return verb;
  return verb.charAt(0).toUpperCase() + verb.slice(1);
}

/**
 * Search / Web search — `{ query | q | search_query }`.
 * Also handles generic tools whose name contains "search".
 */
const searchResolver: PhraseResolver = (input) => {
  const query = pickString(input, ['query', 'q', 'search_query', 'searchQuery']);
  if (query) return `Searching "${clip(query, 50)}"`;
  return 'Searching';
};

/**
 * Calculator — `{ expression | input }`.
 */
const calculatorResolver: PhraseResolver = (input) => {
  const expr = pickString(input, ['expression', 'input', 'formula']);
  if (expr) return `Calculating ${clip(expr, 40)}`;
  return 'Calculating';
};

/**
 * Web fetch / browse — `{ url | uri | href }`.
 * Returns hostname for readability.
 */
const webFetchResolver: PhraseResolver = (input) => {
  const url = pickString(input, ['url', 'uri', 'href']);
  if (!url) return 'Fetching a web page';
  try {
    return `Fetching ${new URL(url).hostname}`;
  } catch {
    return `Fetching ${clip(url, 60)}`;
  }
};

/**
 * Email / send_message — `{ to, subject }`.
 */
const emailResolver: PhraseResolver = (input) => {
  const subject = pickString(input, ['subject']);
  const to = pickString(input, ['to', 'recipient', 'email']);
  if (subject) return `Sending "${clip(subject, 50)}"`;
  if (to) return `Sending to ${clip(to, 40)}`;
  return 'Sending an email';
};

/**
 * Skill activation / deactivation — `{ skillName, skillId }`.
 */
const activateSkillResolver: PhraseResolver = (input) => {
  const name = pickString(input, ['skillName', 'skill_name', 'name']);
  if (name) return `Activating ${clip(name, 40)}`;
  return 'Activating a skill';
};

const deactivateSkillResolver: PhraseResolver = (input) => {
  const name = pickString(input, ['skillName', 'skill_name', 'name']);
  if (name) return `Deactivating ${clip(name, 40)}`;
  return 'Deactivating a skill';
};

/**
 * ask_user — `{ message | prompt | question }`.
 */
const askUserResolver: PhraseResolver = (input) => {
  const msg = pickString(input, ['message', 'prompt', 'question']);
  if (msg) return `Asking: ${clip(msg, 50)}`;
  return 'Asking the user';
};

/**
 * Code execution / shell — `{ command | code | script }`.
 */
const codeResolver: PhraseResolver = (input) => {
  const cmd = pickString(input, ['command', 'code', 'script']);
  if (cmd) return `Running ${clip(cmd.split('\n')[0], 50)}`;
  return 'Running code';
};

/**
 * Knowledge base / RAG — `{ query | question | search }`.
 */
const knowledgeResolver: PhraseResolver = (input) => {
  const query = pickString(input, ['query', 'question', 'search']);
  if (query) return `Searching knowledge for "${clip(query, 40)}"`;
  return 'Searching knowledge base';
};

/**
 * resolve_datetime — `{ expression | datetime | date }`.
 */
const datetimeResolver: PhraseResolver = (input) => {
  const expr = pickString(input, ['expression', 'datetime', 'date', 'input']);
  if (expr) return `Resolving "${clip(expr, 40)}"`;
  return 'Resolving a date/time';
};

// ─── Registry ───────────────────────────────────────────────────────────────

/**
 * Exact-match resolver table, keyed by the tool's display name (case
 * sensitive) or by its built-in tool ID (for skill/ask_user).
 *
 * Platform tool display names match what the backend emits as `toolName`
 * in `agent.run.tool_call` events.
 */
const EXACT_RESOLVERS: Record<string, PhraseResolver> = {
  // Platform tools (display-name keyed)
  'Run Viewer': runViewerResolver,
  'Agent Editor': agentEditorResolver,
  Files: filesResolver,
  Search: searchResolver,
  Calculator: calculatorResolver,

  // Built-in tool IDs
  ask_user: askUserResolver,
  activate_skill: activateSkillResolver,
  deactivate_skill: deactivateSkillResolver,
  resolve_datetime: datetimeResolver,
};

/**
 * Pattern-match fallback — first match wins. Used for custom tools whose
 * display name contains a recognizable keyword. Order matters: put more
 * specific patterns first.
 */
const PATTERN_RESOLVERS: ReadonlyArray<[RegExp, PhraseResolver]> = [
  [/file|read|write|document|upload|download/i, filesResolver],
  [/fetch|browse|scrape|crawl/i, webFetchResolver],
  [/search/i, searchResolver],
  [/calculator|calculate|math/i, calculatorResolver],
  [/email|mail|send_message/i, emailResolver],
  [/knowledge|\bkb\b|rag|embedding/i, knowledgeResolver],
  [/\bcode\b|shell|terminal|run_command/i, codeResolver],
  [/datetime|\bdate\b|\btime\b|resolve/i, datetimeResolver],
  [/skill/i, activateSkillResolver],
];

// ─── Public API ─────────────────────────────────────────────────────────────

/**
 * Returns a complete, human-readable sentence describing a tool call —
 * e.g. "Listing runs", "Retrieving step 5", "Reading README.md".
 *
 * When a phrase is returned, it is the FULL label and should REPLACE the
 * tool's display name in the rendered row.
 *
 * Returns `null` when no friendly phrase can be derived — the caller
 * should fall back to `Using {displayName}...`.
 */
export function getToolDetail(
  toolName: string,
  input: Record<string, unknown>,
): string | null {
  // 1. Exact match on tool display name or built-in tool ID.
  const exact = EXACT_RESOLVERS[toolName];
  if (exact) return exact(input);

  // 2. Pattern match on the tool name.
  for (const [pattern, resolver] of PATTERN_RESOLVERS) {
    if (pattern.test(toolName)) return resolver(input);
  }

  // 3. Generic fallback: if the input has an `operation` or `action` field,
  //    the caller likely wants *some* human label. Produce a minimal sentence
  //    using just the verb, without the object noun.
  const verb = pickString(input, ['operation', 'action', 'method']);
  if (verb) return sentence(verb.replace(/_/g, ' '));

  return null;
}
